import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

// Animated AI workflow diagram. Inputs flow into a central reasoning node,
// outputs flow out. Packets animate along the edges, the center pulses.
// Pure SVG — scales cleanly, no canvas / WebGL.
export default function AIDiagram() {
  const [t, setT] = useState(0);
  const rafRef = useRef(0);
  const startRef = useRef(0);

  useEffect(() => {
    const tick = (ts) => {
      if (!startRef.current) startRef.current = ts;
      setT((ts - startRef.current) / 1000);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const W = 560, H = 480;
  const nodes = {
    in1:  { x: 60,  y: 90,  label: 'Data sources', sub: 'API · DB · Files',     side: 'left'  },
    in2:  { x: 60,  y: 210, label: 'User intent',  sub: 'Chat · Voice · Forms', side: 'left'  },
    in3:  { x: 60,  y: 330, label: 'Events',       sub: 'Webhooks · Signals',   side: 'left'  },
    proc: { x: 250, y: 210, label: 'Reasoning',    sub: 'orchestration',        side: 'mid'   },
    out1: { x: 470, y: 110, label: 'Custom UI',    sub: 'Web · Mobile',         side: 'right' },
    out2: { x: 470, y: 220, label: 'Automation',   sub: 'Workflows',            side: 'right' },
    out3: { x: 470, y: 330, label: 'Insights',     sub: 'Analytics',            side: 'right' },
  };
  const edges = [
    ['in1','proc',0],['in2','proc',1],['in3','proc',2],
    ['proc','out1',3],['proc','out2',4],['proc','out3',5],
  ];
  const exitX = (k) => nodes[k].side === 'left' ? nodes[k].x + 100 : nodes[k].side === 'right' ? nodes[k].x - 60 : nodes[k].x + 60;
  const enterX = (k) => nodes[k].side === 'left' ? nodes[k].x : nodes[k].side === 'right' ? nodes[k].x : nodes[k].x - 60;

  const pathFor = (a, b) => {
    const A = nodes[a], B = nodes[b];
    const ax = exitX(a), bx = enterX(b);
    const mx = (ax + bx) / 2;
    return `M ${ax} ${A.y} C ${mx} ${A.y}, ${mx} ${B.y}, ${bx} ${B.y}`;
  };

  return (
    <div className="ai-diagram">
      <motion.svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height="auto"
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.9, ease: [0.16, 0.84, 0.44, 1] }}
      >
        <defs>
          <pattern id="vt-grid" width="32" height="32" patternUnits="userSpaceOnUse">
            <path d="M 32 0 L 0 0 0 32" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
          </pattern>
          <linearGradient id="vt-edge" x1="0" x2="1">
            <stop offset="0" stopColor="rgba(255,255,255,0.18)" />
            <stop offset="1" stopColor="#FFC107" stopOpacity="0.5" />
          </linearGradient>
          <linearGradient id="vt-edge-out" x1="0" x2="1">
            <stop offset="0" stopColor="#FFC107" stopOpacity="0.5" />
            <stop offset="1" stopColor="rgba(255,255,255,0.18)" />
          </linearGradient>
          <radialGradient id="vt-glow" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0" stopColor="#FFC107" stopOpacity="0.45" />
            <stop offset="1" stopColor="#FFC107" stopOpacity="0" />
          </radialGradient>
        </defs>

        <rect x="0" y="0" width={W} height={H} fill="url(#vt-grid)" opacity="0.7" />
        <circle cx={nodes.proc.x} cy={nodes.proc.y} r="120" fill="url(#vt-glow)" opacity={0.5 + 0.25 * Math.sin(t * 1.6)} />

        {edges.map(([a, b]) => (
          <path key={`e-${a}-${b}`} d={pathFor(a, b)} stroke={a === 'proc' ? 'url(#vt-edge-out)' : 'url(#vt-edge)'} strokeWidth="1.2" fill="none" />
        ))}

        {/* packets */}
        {edges.map(([a, b, i]) => {
          const period = 3.0;
          const p = ((t + i * 0.45) % period) / period;
          const A = nodes[a], B = nodes[b];
          const sx = exitX(a), sy = A.y;
          const ex = enterX(b), ey = B.y;
          const x = sx + (ex - sx) * p;
          const y = sy + (ey - sy) * (3 * p * p - 2 * p * p * p);
          const op = Math.sin(p * Math.PI);
          return (
            <g key={`p-${i}`}>
              <circle cx={x} cy={y} r="3.6" fill="#FFC107" opacity={op} />
              <circle cx={x} cy={y} r="9" fill="#FFC107" opacity={op * 0.18} />
            </g>
          );
        })}

        {/* input nodes */}
        {['in1','in2','in3'].map((k, i) => {
          const n = nodes[k];
          const isActive = Math.floor(t / 1.8) % 3 === i;
          return (
            <g key={k} transform={`translate(${n.x}, ${n.y - 26})`}>
              <rect width="100" height="52" rx="10"
                fill="rgba(23, 23, 27, 0.9)"
                stroke={isActive ? '#FFC107' : 'rgba(255,255,255,0.10)'}
                strokeWidth="1"
                style={{ transition: 'stroke 0.4s' }}
              />
              <text x="12" y="20" fontSize="11" fontFamily="Sora, system-ui" fill="#f4f4f5" fontWeight="500">{n.label}</text>
              <text x="12" y="36" fontSize="9" fontFamily="JetBrains Mono, ui-monospace" fill="#8a8a92" letterSpacing="0.04em">{n.sub}</text>
            </g>
          );
        })}

        {/* center processor */}
        <g transform={`translate(${nodes.proc.x - 64}, ${nodes.proc.y - 52})`}>
          <rect x="-6" y="-6" width="140" height="116" rx="16" fill="none" stroke="#FFC107" strokeWidth="1" opacity={0.3 + 0.3 * Math.sin(t * 2)} />
          <rect width="128" height="104" rx="12" fill="#0d0d0f" stroke="#FFC107" />
          <text x="64" y="34" textAnchor="middle" fontSize="10" fontFamily="JetBrains Mono, ui-monospace" fill="#FFC107" letterSpacing="0.16em">VOILA · AI</text>
          <text x="64" y="60" textAnchor="middle" fontSize="16" fontFamily="Sora, system-ui" fill="#f4f4f5" fontWeight="500">Reasoning</text>
          <text x="64" y="80" textAnchor="middle" fontSize="9" fontFamily="JetBrains Mono, ui-monospace" fill="rgba(244,244,245,0.55)">orchestration · agents</text>
        </g>

        {/* outputs */}
        {['out1','out2','out3'].map((k) => {
          const n = nodes[k];
          return (
            <g key={k} transform={`translate(${n.x}, ${n.y - 26})`}>
              <rect width="100" height="52" rx="10"
                fill="rgba(23, 23, 27, 0.9)"
                stroke="rgba(255,255,255,0.10)"
                strokeWidth="1"
              />
              <text x="12" y="20" fontSize="11" fontFamily="Sora, system-ui" fill="#f4f4f5" fontWeight="500">{n.label}</text>
              <text x="12" y="36" fontSize="9" fontFamily="JetBrains Mono, ui-monospace" fill="#8a8a92" letterSpacing="0.04em">{n.sub}</text>
            </g>
          );
        })}

        {/* corner ticks */}
        <g stroke="rgba(138,138,146,0.45)" strokeWidth="1" fill="none">
          <path d="M 10 10 L 30 10 M 10 10 L 10 30" />
          <path d={`M ${W - 10} 10 L ${W - 30} 10 M ${W - 10} 10 L ${W - 10} 30`} />
          <path d={`M 10 ${H - 10} L 30 ${H - 10} M 10 ${H - 10} L 10 ${H - 30}`} />
          <path d={`M ${W - 10} ${H - 10} L ${W - 30} ${H - 10} M ${W - 10} ${H - 10} L ${W - 10} ${H - 30}`} />
        </g>

        <text x="22" y="22" fontFamily="JetBrains Mono, ui-monospace" fontSize="9" fill="rgba(138,138,146,0.6)" letterSpacing="0.12em">SYS · 0.1.4</text>
        <text x={W - 22} y="22" textAnchor="end" fontFamily="JetBrains Mono, ui-monospace" fontSize="9" fill="rgba(138,138,146,0.6)" letterSpacing="0.12em">LATENCY · 84ms</text>
        <text x="22" y={H - 14} fontFamily="JetBrains Mono, ui-monospace" fontSize="9" fill="rgba(138,138,146,0.6)" letterSpacing="0.12em">MODEL · voila-rt</text>
        <text x={W - 22} y={H - 14} textAnchor="end" fontFamily="JetBrains Mono, ui-monospace" fontSize="9" fill="rgba(138,138,146,0.6)" letterSpacing="0.12em">{`T+${t.toFixed(2)}s`}</text>
      </motion.svg>
    </div>
  );
}
